/**
 * 차트 이벤트 정규화 유틸리티
 * 
 * 모든 차트 타입의 클릭 이벤트를 통일된 $event 형식으로 변환
 * SQL 컬럼 순서 기반의 표준 접근자 제공 (col1, col2, col3, value)
 */

/**
 * ECharts 이벤트를 표준화된 형식으로 변환
 * @param {string} chartType - 차트 타입 (line, bar, pie 등)
 * @param {Object} params - ECharts 이벤트 파라미터
 * @param {Object} chartData - 원본 차트 데이터
 * @returns {Object} 표준화된 $event 객체
 */
export const normalizeEchartsEvent = (chartType, params, chartData) => {
  const chartTypeUpper = chartType ? chartType.toUpperCase() : null;
  const $event = {
    type: chartType || 'unknown',
    rawParams: params,
    timestamp: new Date().toISOString()
  };

  // chartType이 없는 경우 기본 처리
  if (!chartType) {
    console.warn('[normalizeEchartsEvent] chartType is undefined, using default processing');
    $event.col1 = params?.name;
    $event.col2 = params?.value;
    $event.value = params?.value;
    return $event;
  }

  // 차트 타입별 처리
  switch (chartTypeUpper) {
    case 'LINE':
    case 'BAR':
      // 시리즈 데이터 처리
      if (chartData?.data?.series) {
        const isMultiSeries = chartData.data.series.length > 1;
        
        if (isMultiSeries) {
          // 다중 시리즈: 카테고리, 시리즈명, 값
          $event.col1 = params.name || params.axisValue; // X축 (카테고리/시간)
          $event.col2 = params.seriesName; // 시리즈명
          // value가 객체인 경우 실제 값만 추출
          const rawValue = params.value;
          $event.col3 = (typeof rawValue === 'object' && rawValue !== null && 'value' in rawValue)
            ? rawValue.value
            : rawValue;
          $event.value = $event.col3;
        } else {
          // 단일 시리즈: 카테고리, 값
          $event.col1 = params.name || params.axisValue; // X축
          // value가 객체인 경우 실제 값만 추출
          const rawValue = params.value;
          $event.col2 = (typeof rawValue === 'object' && rawValue !== null && 'value' in rawValue)
            ? rawValue.value
            : rawValue;
          $event.value = $event.col2;
        }
        
        // 추가 정보
        $event.series = {
          name: params.seriesName,
          index: params.seriesIndex,
          color: params.color
        };
        
        $event.point = {
          x: params.name || params.axisValue,
          y: params.value,
          index: params.dataIndex
        };
        
        $event.category = params.name;
      }
      break;

    case 'PIE':
      // 파이차트: 이름, 값
      // params.data가 있으면 우선 사용 (ECharts pie 차트에서 전달하는 전체 데이터)
      const pieData = params.data || {};
      $event.col1 = params.name || pieData.name; // 카테고리명
      $event.col2 = params.value || pieData.value; // 값
      $event.value = params.value || pieData.value;
      $event.percent = params.percent;

      // 파이차트용 추가 접근자 (다양한 표현식 지원)
      $event.name = $event.col1;  // 명시적 name 속성
      $event.amount = $event.col2; // 명시적 amount 속성 (value와 동일)
      $event.source = $event.col1; // 데이터 소스명 (예: 에너지원)

      $event.slice = {
        name: $event.col1,
        value: $event.col2,
        percent: params.percent,
        color: params.color
      };

      // 원본 데이터 보존
      if (pieData) {
        $event.data = pieData;
      }

      // 디버깅용 로그
      console.log('[PIE Event] Normalized:', {
        col1: $event.col1,
        col2: $event.col2,
        value: $event.value,
        name: $event.name,
        source: $event.source,
        raw: params
      });
      break;

    case 'SCATTER':
      // 스캐터: X, Y, (크기)
      if (Array.isArray(params.value)) {
        $event.col1 = params.value[0]; // X
        $event.col2 = params.value[1]; // Y
        $event.col3 = params.value[2]; // 크기 (있는 경우)
        $event.value = { x: params.value[0], y: params.value[1] };
      } else {
        $event.col1 = params.value?.x;
        $event.col2 = params.value?.y;
        $event.value = params.value;
      }
      
      $event.point = {
        x: $event.col1,
        y: $event.col2,
        size: $event.col3,
        label: params.name
      };
      break;

    case 'HEATMAP':
      // 히트맵: X, Y, 값
      if (Array.isArray(params.value)) {
        $event.col1 = params.value[0]; // X축 인덱스
        $event.col2 = params.value[1]; // Y축 인덱스
        $event.col3 = params.value[2]; // 값
        $event.value = params.value[2];
      }
      
      $event.cell = {
        x: $event.col1,
        y: $event.col2,
        value: $event.col3
      };
      break;

    case 'GAUGE':
      // 게이지: 값
      $event.col1 = params.value || params.data?.value;
      $event.value = $event.col1;
      
      $event.gauge = {
        value: $event.value,
        name: params.name
      };
      break;

    case 'RADAR':
      // 레이더: 시리즈명, 값 배열
      $event.col1 = params.seriesName;
      $event.col2 = params.value; // 배열
      $event.value = params.value;
      
      $event.radar = {
        series: params.seriesName,
        values: params.value,
        index: params.dataIndex
      };
      break;

    default:
      // 기본 처리
      $event.col1 = params.name;
      $event.col2 = params.value;
      $event.value = params.value;
  }

  // 공통: 데이터 항목에서 원본 SQL 컬럼 배열(__cols)이 있으면 그대로 col1..N을 채움
  try {
    const dataItem = params && (params.data || params.rawData || params.dataItem);
    if (dataItem && typeof dataItem === 'object') {
      if (Array.isArray(dataItem.__cols) && dataItem.__cols.length > 0) {
        dataItem.__cols.forEach((v, i) => { $event[`col${i + 1}`] = v; });
      }
      if (dataItem.__row && typeof dataItem.__row === 'object') {
        $event.row = dataItem.__row;
      }
    }
  } catch (_) {}

  // 공통 속성 추가
  $event.seriesName = params.seriesName;
  $event.dataIndex = params.dataIndex;

  return $event;
};

/**
 * 테이블 클릭 이벤트 표준화
 * @param {Object} rowData - 클릭된 행 데이터
 * @param {number} rowIndex - 행 인덱스
 * @param {Array} columns - 컬럼 정의
 * @returns {Object} 표준화된 $event 객체
 */
export const normalizeTableEvent = (rowData, rowIndex, columns = []) => {
  const $event = {
    type: 'table',
    timestamp: new Date().toISOString()
  };

  // 전체 행 데이터
  $event.row = rowData;
  $event.rowIndex = rowIndex;
  $event.value = rowData; // 테이블에서는 전체 행이 값

  // 컬럼 순서 기반 접근 - 모든 컬럼을 colN으로 매핑
  if (columns.length > 0) {
    columns.forEach((col, idx) => {
      const key = typeof col === 'object' ? (col?.dataIndex || col?.key || col?.name) : col;
      $event[`col${idx + 1}`] = Array.isArray(rowData) ? rowData[idx] : rowData[key];
    });
  } else {
    // 컬럼 정보가 없으면 객체 키 순서 사용
    const keys = Array.isArray(rowData) ? rowData.map((_, i) => i) : Object.keys(rowData);
    keys.forEach((k, idx) => {
      $event[`col${idx + 1}`] = Array.isArray(rowData) ? rowData[idx] : rowData[k];
    });
  }

  // 컬럼명 정보
  $event.colNames = Array.isArray(columns) && columns.length > 0
    ? columns.map(col => (typeof col === 'object' ? (col.dataIndex || col.key || col.name || String(col)) : col))
    : (Array.isArray(rowData) ? $event.colNames || [] : Object.keys(rowData));

  return $event;
};

/**
 * 배지 클릭 이벤트 표준화
 * @param {Object} badgeData - 배지 데이터
 * @returns {Object} 표준화된 $event 객체
 */
export const normalizeBadgeEvent = (badgeData) => {
  const $event = {
    type: 'badge',
    timestamp: new Date().toISOString()
  };

  // 배지는 단일 값
  $event.col1 = badgeData.value;
  $event.value = badgeData.value;
  
  // 추가 정보
  $event.badge = {
    value: badgeData.value,
    unit: badgeData.unit,
    label: badgeData.seriesName || badgeData.label,
    title: badgeData.title
  };

  return $event;
};

/**
 * 텍스트 라벨 클릭 이벤트 표준화
 * @param {Object} labelData - 라벨 데이터
 * @returns {Object} 표준화된 $event 객체
 */
export const normalizeTextLabelEvent = (labelData) => {
  const $event = {
    type: 'text_label',
    timestamp: new Date().toISOString()
  };

  // 텍스트 라벨은 텍스트가 값
  $event.col1 = labelData.text || labelData.value;
  $event.value = $event.col1;
  
  $event.label = {
    text: $event.value,
    title: labelData.title
  };

  return $event;
};

/**
 * 지도 마커 클릭 이벤트 표준화
 * @param {Object} markerData - 마커 데이터
 * @returns {Object} 표준화된 $event 객체
 */
export const normalizeMapEvent = (markerData) => {
  const $event = {
    type: 'map',
    timestamp: new Date().toISOString()
  };

  // 지도: 위도, 경도, (제목)
  $event.col1 = markerData.lat || markerData.latitude;
  $event.col2 = markerData.lng || markerData.longitude;
  $event.col3 = markerData.title || markerData.name || markerData.id;
  $event.value = { lat: $event.col1, lng: $event.col2 };

  $event.marker = {
    id: markerData.id,
    lat: $event.col1,
    lng: $event.col2,
    title: $event.col3,
    properties: markerData.properties || {}
  };

  return $event;
};

/**
 * 배터리 그리드 클릭 이벤트 표준화
 * @param {Object} eventData - 배터리 그리드 이벤트 데이터
 * @returns {Object} 표준화된 $event 객체
 */
export const normalizeBatteryGridEvent = (eventData) => {
  const $event = {
    type: 'battery_grid',
    timestamp: new Date().toISOString()
  };

  // 배터리 그리드 전용 속성들
  $event.batteryId = eventData.batteryId;
  $event.soc = eventData.soc;
  $event.soh = eventData.soh;
  $event.state = eventData.state;
  $event.dataIndex = eventData.dataIndex;
  
  // 표준 컬럼 매핑 (SQL 순서: batt_id, soc, soh, state)
  $event.col1 = eventData.batteryId;  // batt_id
  $event.col2 = eventData.soc;         // soc
  $event.col3 = eventData.soh;         // soh
  $event.col4 = eventData.state;       // state
  
  // 기본 value는 SOC로 설정
  $event.value = eventData.soc;
  $event.raw = eventData;

  // 추가 컬럼을 col5..로 매핑 (가능한 경우)
  const fromExtraColumns = Array.isArray(eventData.extraColumns) ? eventData.extraColumns : null;
  if (fromExtraColumns && fromExtraColumns.length > 0) {
    fromExtraColumns.forEach((columnName, idx) => {
      const valueFromExtras = eventData.extras && Object.prototype.hasOwnProperty.call(eventData.extras, columnName)
        ? eventData.extras[columnName]
        : undefined;
      if (valueFromExtras !== undefined) {
        $event[`col${5 + idx}`] = valueFromExtras;
      }
    });
  } else {
    // 과거 extraN 필드가 직접 있는 경우에도 col5..로 연결
    const extraKeys = Object.keys(eventData || {}).filter(key => /^extra\d+$/.test(key));
    extraKeys
      .sort((a, b) => parseInt(a.replace('extra', ''), 10) - parseInt(b.replace('extra', ''), 10))
      .forEach((alias, idx) => {
        $event[`col${5 + idx}`] = eventData[alias];
      });
  }

  return $event;
};

/**
 * DatePicker 날짜 선택 이벤트 표준화
 * @param {Object} eventData - DatePicker 이벤트 데이터
 * @returns {Object} 표준화된 $event 객체
 */
export const normalizeDatePickerEvent = (eventData) => {
  const $event = {
    type: 'date_picker',
    timestamp: new Date().toISOString()
  };

  // DatePicker 전용 속성들
  $event.startDate = eventData.startDate;
  $event.endDate = eventData.endDate;
  $event.startDateTime = eventData.startDateTime;
  $event.endDateTime = eventData.endDateTime;
  $event.range = eventData.range;
  
  // 표준 컬럼 매핑
  $event.col1 = eventData.startDate;  // 시작 날짜
  $event.col2 = eventData.endDate;    // 종료 날짜
  $event.col3 = eventData.range;      // 날짜 범위 문자열
  
  // value는 범위 객체 또는 단일 날짜
  $event.value = eventData.value || {
    start: eventData.startDate,
    end: eventData.endDate
  };
  
  // 원본 이벤트 데이터
  $event.raw = eventData;

  return $event;
};

/**
 * 통합 이벤트 정규화 함수
 * @param {string} chartType - 차트 타입
 * @param {Object} eventData - 원본 이벤트 데이터
 * @param {Object} chartData - 차트 전체 데이터 (옵션)
 * @returns {Object} 표준화된 $event 객체
 */
export const normalizeChartEvent = (chartType, eventData, chartData = null) => {
  // chartType이 없거나 undefined인 경우 처리
  if (!chartType) {
    console.warn('[normalizeChartEvent] chartType is undefined or null, returning default event');
    return {
      type: 'unknown',
      col1: eventData?.value,
      value: eventData?.value || eventData,
      raw: eventData,
      timestamp: new Date().toISOString()
    };
  }
  
  const type = chartType.toUpperCase();
  
  switch (type) {
    case 'TABLE':
      // TableRenderer에서 이미 col1, col2, col3로 전달하는 경우
      if (eventData.col1 !== undefined || eventData.col2 !== undefined || eventData.col3 !== undefined) {
        console.log('[chartEventNormalizer] Table event already normalized:', eventData);
        return {
          ...eventData,
          type: 'table',
          timestamp: new Date().toISOString()
        };
      }
      // 기존 방식 (row 데이터로 전달하는 경우)
      return normalizeTableEvent(
        eventData.row || eventData,
        eventData.rowIndex || 0,
        eventData.columns || []
      );
      
    case 'BADGE':
      return normalizeBadgeEvent(eventData);
      
    case 'TEXT_LABEL':
      return normalizeTextLabelEvent(eventData);
      
    case 'NAVERMAP':
    case 'MAP':
      return normalizeMapEvent(eventData);
      
    case 'BATTERY_GRID':
      return normalizeBatteryGridEvent(eventData);
      
    case 'DATE_PICKER':
      return normalizeDatePickerEvent(eventData);
      
    case 'LINE':
    case 'BAR':
    case 'PIE':
    case 'SCATTER':
    case 'HEATMAP':
    case 'GAUGE':
    case 'RADAR':
      return normalizeEchartsEvent(type, eventData, chartData);
      
    default:
      // 알 수 없는 타입은 기본 처리
      return {
        type: chartType,
        col1: eventData.value,
        value: eventData.value,
        raw: eventData,
        timestamp: new Date().toISOString()
      };
  }
};

/**
 * 표현식 평가 헬퍼
 * on_click의 publish 값에서 사용되는 표현식 평가
 * @param {string} expression - "$event.col1" 같은 표현식
 * @param {Object} $event - 이벤트 객체
 * @returns {*} 평가된 값
 */
export const evaluateEventExpression = (expression, $event) => {
  if (typeof expression !== 'string') {
    return expression;
  }

  // $event 참조 처리
  if (expression.startsWith('$event.')) {
    const path = expression.substring(7).split('.');
    let value = $event;
    
    for (const key of path) {
      if (value && typeof value === 'object') {
        value = value[key];
      } else {
        return null;
      }
    }
    
    return value;
  }

  // 조건식 평가 (삼항 연산자 등)
  if (expression.includes('?') || expression.includes('>') || expression.includes('<')) {
    try {
      // 안전한 평가를 위한 샌드박스
      const func = new Function('$event', `
        try {
          return ${expression};
        } catch (e) {
          return null;
        }
      `);
      return func($event);
    } catch (error) {
      console.warn('[EventNormalizer] Expression evaluation failed:', expression, error);
      return null;
    }
  }

  // 단순 문자열이나 숫자
  return expression;
};

export default {
  normalizeChartEvent,
  normalizeEchartsEvent,
  normalizeTableEvent,
  normalizeBadgeEvent,
  normalizeTextLabelEvent,
  normalizeMapEvent,
  normalizeBatteryGridEvent,
  normalizeDatePickerEvent,
  evaluateEventExpression
};
